import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./Careers.css";

export default function Careers() {
  const [activeIndex, setActiveIndex] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // 🔍 Search
  const [searchText, setSearchText] = useState("");

  // 📄 Resume file
  const [resumeFile, setResumeFile] = useState(null);

  // 🧾 Form data
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    message: "",
  });

  // 🔁 Convert file to Base64
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () =>
        resolve(reader.result.split(",")[1]); // remove data prefix
      reader.onerror = reject;
    });

  const sections = [
    {
      title: "Open Positions",
      icon: "💼",
      tag: "Hiring",
      short: "Explore current job openings at WeMindSolutions.",
      roles: [
        { name: "Frontend Developer (React)", location: "Remote / India", type: "Full Time" },
        { name: "Backend Developer (Node.js)", location: "Remote", type: "Full Time" },
        { name: "UI/UX Designer", location: "Hybrid", type: "Contract" },
      ],
    },
    {
      title: "Internships",
      icon: "🎓",
      tag: "Students",
      short: "Kickstart your career with hands-on experience.",
      roles: [
        { name: "React Developer Intern", duration: "3 – 6 Months" },
        { name: "Digital Marketing Intern", duration: "3 Months" },
      ],
    },
    {
      title: "Why Work With Us",
      icon: "⭐",
      tag: "Culture",
      short: "Discover why WeMindSolutions is a great workplace.",
      roles: [],
    },
  ];

  const toggleSection = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
    setSearchText("");
  };

  const openModal = (roleName) => {
    setSelectedRole(roleName);
    setSubmitted(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSubmitted(false);
    setResumeFile(null);
    setFormData({ fullName: "", email: "", message: "" });
  };

  return (
    <div className="careers-page">
      {/* HERO */}
      <section className="careers-hero">
        <h1>Careers at WeMindSolutions</h1>
        <p>Join our team to shape the future of technology.</p>
      </section>

      {/* SECTIONS */}
      <section className="careers-section">
        <div className="careers-container">
          {sections.map((section, i) => {
            const isActive = activeIndex === i;

            const filteredRoles = section.roles.filter((role) =>
              role.name.toLowerCase().includes(searchText.toLowerCase())
            );

            return (
              <motion.div
                key={i}
                layout
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
                onClick={() => toggleSection(i)}
                className={`career-card ${isActive ? "active" : ""}`}
              >
                <div className="career-header">
                  <h2>
                    <span className="icon">{section.icon}</span>
                    {section.title}
                  </h2>
                  <span className="tag">{section.tag}</span>
                </div>

                <p className="career-short">{section.short}</p>

                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="career-full"
                    >
                      {section.roles.length > 0 && (
                        <input
                          className="role-search"
                          placeholder={`Search ${section.title}...`}
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}

                      {section.roles.length > 0 ? (
                        <div className="roles-list">
                          {filteredRoles.map((role, idx) => (
                            <div key={idx} className="role-card">
                              <div>
                                <h4>{role.name}</h4>
                                <div className="role-meta">
                                  {role.location && <span>📍 {role.location}</span>}
                                  {role.type && <span>⏱ {role.type}</span>}
                                  {role.duration && <span>⏳ {role.duration}</span>}
                                </div>
                              </div>
                              <button
                                className="apply-btn small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openModal(role.name);
                                }}
                              >
                                Apply
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="culture-text">
                          We foster a people-first culture with learning opportunities and flexibility.
                        </p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                <span className="career-toggle">{isActive ? "Show Less" : "Read More"}</span>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* APPLY MODAL */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div className="modal-overlay">
            <motion.div className="modal-content">
              {!submitted ? (
                <>
                  <h2>Apply for {selectedRole}</h2>

                  <form
                    className="apply-form"
                    onSubmit={async (e) => {
                      e.preventDefault();

                      if (!resumeFile) {
                        alert("Please upload resume");
                        return;
                      }

                      try {
                        const fileBase64 = await toBase64(resumeFile);

                        await fetch(
                          "https://33281a46df83eb48be0d564bfdb47d.d3.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/1e674032606041f68a2b6acb0362c6ab/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=4mTHSadvj8TXzsIYdM8FlWoM0Phl2w6kJPpPzzJROlA",
                          {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              fullName: formData.fullName,
                              email: formData.email,
                              role: selectedRole,
                              message: formData.message,
                              fileName: resumeFile.name,
                              fileContent: fileBase64,
                            }),
                          }
                        );

                        setSubmitted(true);
                      } catch (err) {
                        alert("Submission failed");
                      }
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Full Name"
                      required
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    />

                    <input
                      type="email"
                      placeholder="Email Address"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />

                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      required
                      onChange={(e) => setResumeFile(e.target.files[0])}
                    />

                    <textarea
                      placeholder="Message"
                      rows="4"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    />

                    <div className="modal-buttons">
                      <button type="submit" className="submit-btn">Submit</button>
                      <button type="button" className="cancel-btn" onClick={closeModal}>Cancel</button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="success-box">
                  🎉 Application Submitted!
                  <p>We’ll contact you soon.</p>
                  <button onClick={closeModal}>Close</button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
